import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { PrimaryButton, SecondaryButton, useDialog } from 'ogp/providers/dialog-provider';
import { IconApproved, IconCancel } from 'assets/img';
import { accreditationsAlerts, useAlert } from 'shared/components/alerts';
import { useRejectAccreditation } from '../../hooks/use-reject-accreditation';
import type { AccreditationRejectionReason } from '../../../../../services/accreditations-service.types';
import {
  AccreditationDetailFormValues,
  RejectAccreditationButtonProps,
} from '../accreditations-detail.types';

export const useAccreditationDetailRejectAction = ({
  flexWorkerId,
  certificateId,
  accreditationType,
  rejectionReason,
  closeModal,
}: RejectAccreditationButtonProps) => {
  const { t } = useTranslation(['hq-screen-specific', 'web-common']);
  const { openDialog, closeDialog, enableDialog, disableDialog } = useDialog();
  const { alertSuccess, alertError } = useAlert();
  const mutation = useRejectAccreditation();
  const { setError } = useFormContext<AccreditationDetailFormValues>();

  const handleRejectAccreditation = () => {
    const requiredMessage = t('certificates.detail.rejection-reason.required');

    if (!rejectionReason) {
      setError('rejectionReason', { type: 'required', message: requiredMessage });
      return;
    }

    openDialog(
      {
        title: t('certificates.detail.reject-confirm.title'),
        secondaryButton: (props) => (
          <PrimaryButton iconColorMode="fill" startIcon={<IconCancel />} {...props}>
            {t('buttons.cancel', { ns: 'web-common' })}
          </PrimaryButton>
        ),
        primaryButton: (props) => (
          <SecondaryButton startIcon={<IconApproved />} {...props}>
            {t('buttons.confirm', { ns: 'web-common' })}
          </SecondaryButton>
        ),
      },
      () => {
        disableDialog();
        mutation.mutate(
          {
            flexWorkerId,
            certificateId,
            accreditationType,
            rejectionReason: rejectionReason as AccreditationRejectionReason,
          },
          {
            onSuccess: () => {
              closeDialog();
              closeModal();
              alertSuccess(accreditationsAlerts.success.rejected);
            },
            onError: (e) => alertError(e),
            onSettled: () => {
              enableDialog();
            },
          }
        );
      }
    );
  };

  return {
    handleRejectAccreditation,
    rejectButtonLabel: t('certificates.detail.reject-button.label'),
  };
};
